import React, { useEffect, useState } from "react";
import apiClient from "../../lib/api";
import { useI18n } from "../../i18n";
import { toast } from "sonner";
import { Save, Image as ImageIcon, X } from "lucide-react";

const AdminHomepage = () => {
  const { lang } = useI18n();
  const [hp, setHp] = useState(null);
  const [media, setMedia] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showLib, setShowLib] = useState(false);

  useEffect(() => {
    apiClient.get("/admin/settings").then((r) => setHp(r.data.homepage));
    apiClient.get("/admin/media").then((r) => setMedia(r.data));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await apiClient.put("/admin/settings", { homepage: hp });
      toast.success(lang === "nl" ? "Opgeslagen" : "Saved");
    } catch (e) { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  if (!hp) return <div className="text-ink-muted">Loading…</div>;
  const upd = (k, v) => setHp({ ...hp, [k]: v });

  return (
    <div data-testid="admin-homepage-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
        <h1 className="font-display text-4xl md:text-5xl tracking-tighter font-light">{lang === "nl" ? "Homepage" : "Homepage"}</h1>
        <button onClick={save} disabled={busy} className="btn-primary text-sm" data-testid="homepage-save"><Save className="w-4 h-4" /> {busy ? "…" : (lang === "nl" ? "Opslaan" : "Save")}</button>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="border border-black/10 bg-white p-6 space-y-3">
          <div className="font-label text-ink-muted">{lang === "nl" ? "Hero — Nederlands" : "Hero — Dutch"}</div>
          <Field label="Title NL" value={hp.hero_title_nl} onChange={(v) => upd("hero_title_nl", v)} testid="hero-title-nl" />
          <Textarea label="Subtitle NL" value={hp.hero_subtitle_nl} onChange={(v) => upd("hero_subtitle_nl", v)} testid="hero-subtitle-nl" />
          <Field label="CTA label NL" value={hp.hero_cta_label_nl} onChange={(v) => upd("hero_cta_label_nl", v)} testid="hero-cta-nl" />
        </div>
        <div className="border border-black/10 bg-white p-6 space-y-3">
          <div className="font-label text-ink-muted">Hero — English</div>
          <Field label="Title EN" value={hp.hero_title_en} onChange={(v) => upd("hero_title_en", v)} testid="hero-title-en" />
          <Textarea label="Subtitle EN" value={hp.hero_subtitle_en} onChange={(v) => upd("hero_subtitle_en", v)} testid="hero-subtitle-en" />
          <Field label="CTA label EN" value={hp.hero_cta_label_en} onChange={(v) => upd("hero_cta_label_en", v)} testid="hero-cta-en" />
        </div>
      </div>

      <div className="border border-black/10 bg-white p-6 mt-6 space-y-3">
        <div className="font-label text-ink-muted">{lang === "nl" ? "Hero afbeelding" : "Hero image"}</div>
        <div className="flex items-start gap-4">
          <div className="w-48 h-32 bg-secondary-warm overflow-hidden"><img src={hp.hero_image} alt="" className="w-full h-full object-cover" /></div>
          <div className="flex-1 space-y-2">
            <input value={hp.hero_image} onChange={(e) => upd("hero_image", e.target.value)} placeholder="https://…" className="border border-black/15 px-3 py-2 text-sm w-full" data-testid="hero-image-url" />
            <button onClick={() => setShowLib(true)} className="btn-secondary text-xs"><ImageIcon className="w-3.5 h-3.5" /> {lang === "nl" ? "Kies uit mediatheek" : "Choose from media library"}</button>
          </div>
        </div>
      </div>

      <div className="border border-black/10 bg-white p-6 mt-6">
        <div className="font-label text-ink-muted mb-3">{lang === "nl" ? "Marquee berichten (één per regel)" : "Marquee messages (one per line)"}</div>
        <textarea
          rows={6}
          value={(hp.marquee_messages || []).join("\n")}
          onChange={(e) => upd("marquee_messages", e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))}
          className="border border-black/15 px-3 py-2 text-sm w-full font-mono"
          data-testid="marquee-messages"
        />
      </div>

      {showLib && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowLib(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white max-w-4xl w-full max-h-[80vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center mb-4"><div className="font-display text-2xl">{lang === "nl" ? "Mediatheek" : "Media library"}</div><button onClick={() => setShowLib(false)}><X className="w-5 h-5" /></button></div>
            <div className="grid grid-cols-4 md:grid-cols-6 gap-3">
              {media.map((m) => (
                <button key={m.media_id} onClick={() => { upd("hero_image", m.url); setShowLib(false); }} className="aspect-square bg-secondary-warm hover:ring-2 hover:ring-ink overflow-hidden">
                  <img src={m.url} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value, onChange, testid }) => (
  <label className="block">
    <div className="text-xs font-label text-ink-muted mb-1">{label}</div>
    <input data-testid={testid} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="border border-black/15 px-3 py-2 text-sm w-full bg-white" />
  </label>
);
const Textarea = ({ label, value, onChange, testid }) => (
  <label className="block">
    <div className="text-xs font-label text-ink-muted mb-1">{label}</div>
    <textarea data-testid={testid} rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} className="border border-black/15 px-3 py-2 text-sm w-full bg-white" />
  </label>
);

export default AdminHomepage;
